import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
  Divider,
  CircularProgress,
  Avatar,
  IconButton,
} from '@mui/material';
import { format } from 'date-fns';
import { uk } from 'date-fns/locale';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { CalendarEventDto } from '@/types/calendar';
import { CalendarEventDetails } from './CalendarEventDetails';
import { EventTypeIcon } from './EventTypeIcon';
import { ModalIndices } from '../../../common/i-doc/i-doc.registry.ts';

interface CalendarEventDetailModalProps {
  open: boolean;
  event: CalendarEventDto | null;
  loading: boolean;
  onClose: () => void;
  onEdit?: (event: CalendarEventDto) => void;
  onDelete?: (event: CalendarEventDto) => void;
  onManageAppointment?: (appointmentId: number) => void;
}

export const CalendarEventDetailModal: React.FC<CalendarEventDetailModalProps> = ({
  open,
  event,
  loading,
  onClose,
  onEdit,
  onDelete,
  onManageAppointment,
}) => {
  const canEdit = event?.actions.includes('edit') ?? false;
  const canDelete = event?.actions.includes('delete') ?? false;
  const isAppointment = event?.associationType === 'Appointment' && !!event?.associationId;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth data-i-doc={ModalIndices.CALENDAR_EVENT_DETAIL}>
      {event?.eventType.color && (
        <Box sx={{ height: '4px', backgroundColor: event.eventType.color, flexShrink: 0 }} />
      )}
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between', pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1} minWidth={0}>
          {event && (
            <EventTypeIcon
              code={event.eventType.code}
              icon={event.eventType.icon}
              accentColor={event.eventType.color}
              size={22}
            />
          )}
          <Typography variant="h6" component="div" noWrap>
            {loading ? 'Завантаження...' : (event?.eventType?.name || 'Подія')}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ flexShrink: 0 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {!loading && event && (
          <Box>
            {/* === Основний контент === */}

            {/* Status */}
            <Box display="flex" alignItems="center" gap={0.5} mb={2}>
              <Checkbox
                checked={event.isCompleted}
                size="small"
                disableRipple
                icon={<RadioButtonUncheckedIcon fontSize="small" />}
                checkedIcon={<CheckCircleIcon fontSize="small" />}
                sx={{ p: 0, color: 'warning.main', '&.Mui-checked': { color: 'success.main' } }}
              />
              <Typography variant="body2" color={event.isCompleted ? 'success.main' : 'warning.main'}>
                {event.isCompleted ? 'Виконано' : 'Очікується'}
              </Typography>
            </Box>

            {/* Title */}
            {event.title && (
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary" gutterBottom>Опис</Typography>
                <Typography variant="body1">{event.title}</Typography>
              </Box>
            )}

            {/* Type-specific details */}
            <CalendarEventDetails event={event} />

            <Divider sx={{ my: 2 }} />

            {/* === Допоміжні поля === */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 16px', alignItems: 'center' }}>
              {event.scheduledLdt && (
                <>
                  <Typography variant="body2" color="text.secondary">Заплановано</Typography>
                  <Typography variant="body2">
                    {format(new Date(event.scheduledLdt), 'dd MMMM yyyy, HH:mm', { locale: uk })}
                  </Typography>
                </>
              )}
              {event.completedAt && (
                <>
                  <Typography variant="body2" color="text.secondary">Виконано</Typography>
                  <Typography variant="body2">
                    {format(new Date(event.completedAt), 'dd MMMM yyyy, HH:mm', { locale: uk })}
                  </Typography>
                </>
              )}
              <Typography variant="body2" color="text.secondary">Автор</Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <Avatar src={event.createdBy.avatar || undefined} sx={{ width: 18, height: 18, fontSize: '10px' }}>
                  {event.createdBy.name.charAt(0)}
                </Avatar>
                <Typography variant="body2">{event.createdBy.name}</Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">Створено</Typography>
              <Typography variant="caption" color="text.secondary">
                {event.createdAt ? format(new Date(event.createdAt), 'dd.MM.yyyy HH:mm') : '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary">Оновлено</Typography>
              <Typography variant="caption" color="text.secondary">
                {event.updatedAt ? format(new Date(event.updatedAt), 'dd.MM.yyyy HH:mm') : '—'}
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      {(canEdit || canDelete || (isAppointment && onManageAppointment)) && (
        <DialogActions sx={{ justifyContent: 'flex-start', px: 3 }}>
          {isAppointment && onManageAppointment && event && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => { onManageAppointment(event.associationId!); onClose(); }}
            >
              Керувати зустріччю
            </Button>
          )}
          {canEdit && onEdit && event && (
            <Button startIcon={<EditIcon />} size="small" onClick={() => { onEdit(event); onClose(); }}>
              Редагувати
            </Button>
          )}
          {canDelete && onDelete && event && (
            <Button startIcon={<DeleteIcon />} size="small" color="error" onClick={() => { onDelete(event); onClose(); }}>
              Видалити
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};
